import { AlertTriangle } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { cn } from '@/lib/utils'

/**
 * #073 — Environment banner + switcher. Superuser-only.
 *
 * Build-time config (Vite, baked into the bundle):
 *   VITE_ENV_NAME       'staging' | 'production' — drives the label + styling
 *   VITE_OTHER_ENV_URL  the OTHER environment's dashboard URL (the switch target)
 *
 * Returns null for non-superusers and when VITE_ENV_NAME is unset, so it is
 * invisible to staff and customers. Text is intentionally English-only — this is
 * an internal operator aid, not customer-facing UI.
 */
export function EnvironmentBanner() {
  const isSuperuser = useAuthStore((s) => s.user?.is_superuser)
  const envName = import.meta.env.VITE_ENV_NAME
  const otherUrl = import.meta.env.VITE_OTHER_ENV_URL

  if (!isSuperuser || !envName) return null

  const isStaging = envName.toLowerCase() === 'staging'
  const switchTo = isStaging ? 'Production' : 'Staging'

  return (
    <div
      role="status"
      className={cn(
        // #393 — phones: 11 px under 360 px and never two lines
        'flex shrink-0 items-center justify-center gap-2 border-b px-4 py-1 text-[11px] min-[360px]:text-xs font-semibold tracking-wide whitespace-nowrap',
        isStaging
          ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40'
          : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30'
      )}
    >
      {isStaging && <AlertTriangle className="h-3.5 w-3.5 shrink-0" />}
      <span className="uppercase">{isStaging ? 'Staging environment' : 'Production'}</span>
      {otherUrl && (
        <>
          <span aria-hidden className="opacity-50">·</span>
          <a
            href={otherUrl}
            className="font-medium underline underline-offset-2 hover:opacity-80"
          >
            Switch to {switchTo} →
          </a>
        </>
      )}
    </div>
  )
}
